import { useState, useRef, useEffect } from "react";
import "./distribution.css";

export type DistributionSample = {
  label: string;
  value: number | null;
  highlighted?: boolean;
};
/** One independent quantitative axis; null is not zero. Labels are supplied by the consumer. */
export function DistributionPlot({
  label,
  samples,
  median,
  medianLabel = "중앙값",
  reference,
  referenceLabel = "비교 값",
  maximum,
  format = String,
}: {
  label: string;
  samples: DistributionSample[];
  median: number | null;
  medianLabel?: string;
  reference?: number | null;
  referenceLabel?: string;
  maximum?: number;
  format?: (value: number) => string;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(300);
  useEffect(() => {
    const node = container.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) =>
      setWidth(Math.max(160, entries[0].contentRect.width)),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const valid = samples.filter((s) => s.value != null);
  const max =
    maximum ??
    Math.max(1, ...valid.map((s) => s.value!), median ?? 0, reference ?? 0) *
      1.12;
  const x = (v: number) =>
    12 + Math.max(0, Math.min(1, v / max)) * (width - 24);
  return (
    <div
      ref={container}
      className="distribution"
      role="group"
      aria-label={label + " 분포"}
    >
      {!valid.length ? (
        <div className="distribution-empty">분석된 값 없음</div>
      ) : (
        <>
          <svg
            viewBox={"0 0 " + width + " 54"}
            role="img"
            aria-label={
              label +
              ": 점은 개별 항목, 세로선은 " +
              medianLabel +
              (reference != null ? ", 마름모는 " + referenceLabel : "")
            }
          >
            <line
              x1="12"
              x2={width - 12}
              y1="24"
              y2="24"
              className="distribution-axis"
            />
            {median != null && (
              <line
                x1={x(median)}
                x2={x(median)}
                y1="5"
                y2="42"
                className="distribution-median"
              >
                <title>
                  {medianLabel}: {format(median)}
                </title>
              </line>
            )}
            {samples.map((s, i) =>
              s.value == null ? null : (
                <circle
                  key={i}
                  cx={x(s.value)}
                  cy={18 + (i % 3) * 6}
                  r={selected === i ? 5 : 3.5}
                  className={
                    s.highlighted
                      ? "distribution-point is-highlight"
                      : "distribution-point"
                  }
                >
                  <title>
                    {s.label}: {format(s.value)}
                  </title>
                </circle>
              ),
            )}
            {reference != null && (
              <path
                className="distribution-reference"
                d={"M " + x(reference) + " 15 l 7 9 -7 9 -7 -9 Z"}
              >
                <title>
                  {referenceLabel}: {format(reference)}
                </title>
              </path>
            )}
          </svg>
          <div className="distribution-range">
            <span>0</span>
            <span>{format(max)}</span>
          </div>
          <details>
            <summary>개별 값 {valid.length}개</summary>
            <div className="distribution-values">
              {samples.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  aria-pressed={selected === i}
                  onClick={() => setSelected(i)}
                >
                  {s.label} <b>{s.value == null ? "—" : format(s.value)}</b>
                </button>
              ))}
            </div>
          </details>
        </>
      )}
    </div>
  );
}
